import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { Loader2, Send, Search, Users, UserCheck, Check, ArrowLeft } from "lucide-react";
import { useGetStudentsQuery } from "@/redux/slices/studentApi";
import { getApiUrl } from "@/lib/utils";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { PageHeader } from "@/components/common/PageHeader";

export default function SendNotification() {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState<"all" | "selected">("all");
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSending, setIsSending] = useState(false);

  const token = useSelector((state: RootState) => state.auth.token);
  const tenantId = import.meta.env.VITE_TENANT_ID || 'ayursangrah';

  const { data: studentsResponse, isLoading: loadingStudents } = useGetStudentsQuery({ limit: 1000 });
  const students = studentsResponse?.data || [];

  const filteredStudents = useMemo(() => {
    return students.filter(
      (s) =>
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [students, searchTerm]);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Please enter a title and message body.");
      return;
    }
    if (target === "selected" && studentIds.length === 0) {
      toast.error("Please select at least one student.");
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/notifications/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId
        },
        body: JSON.stringify({
          title,
          body,
          target,
          studentIds: target === "selected" ? studentIds : []
        })
      });

      const result = await response.json();
      if (result.success) {
        toast.success(result.message || "Notification sent successfully.");
        setTitle("");
        setBody("");
        setStudentIds([]);
        navigate('/notifications');
      } else {
        toast.error(result.message || "Failed to send notification.");
      }
    } catch (error) {
      toast.error("Server error while sending notification.");
    } finally {
      setIsSending(false);
    }
  };

  const selectAll = () => {
    if (studentIds.length === filteredStudents.length) {
      setStudentIds([]);
    } else {
      setStudentIds(filteredStudents.map((s) => s.id));
    }
  };

  return (
    <div className="min-h-screen bg-muted/20 animate-in fade-in duration-500">
      <PageHeader
        title="Compose Notification"
        description="Broadcast announcements or send targeted updates instantly."
        action={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        }
      />
      
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Column - Compose */}
          <div className="lg:col-span-7 space-y-6">
            <Card className="border shadow-sm">
              <CardHeader className="bg-muted/30 border-b pb-4">
                <CardTitle className="flex items-center text-lg">
                  <Send className="w-5 h-5 mr-2 text-primary" />
                  Message Details
                </CardTitle>
                <CardDescription>Design your push notification below.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5 pt-6">
                <div className="space-y-2.5">
                  <Label htmlFor="title" className="font-semibold text-foreground">Notification Title</Label>
                  <Input
                    id="title"
                    placeholder="E.g. 🚨 Important Update: New Class Schedule"
                    className="h-11 focus-visible:ring-primary/50 text-base"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>
                <div className="space-y-2.5">
                  <Label htmlFor="body" className="font-semibold text-foreground">Message Body</Label>
                  <Textarea
                    id="body"
                    placeholder="Write your message here... Make it clear and concise."
                    className="min-h-[140px] resize-none focus-visible:ring-primary/50 text-base"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Audience */}
          <div className="lg:col-span-5 space-y-6">
            <Card className="border shadow-sm h-full flex flex-col">
              <CardHeader className="bg-muted/30 border-b pb-4">
                <CardTitle className="flex items-center text-lg">
                  <Users className="w-5 h-5 mr-2 text-primary" />
                  Target Audience
                </CardTitle>
                <CardDescription>Select who should receive this update.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6 flex-1">
                <RadioGroup
                  value={target}
                  onValueChange={(value) => setTarget(value as "all" | "selected")}
                  className="grid grid-cols-1 gap-3"
                >
                  <Label
                    htmlFor="target-all"
                    className={`flex items-center space-x-3 border p-4 rounded-xl cursor-pointer transition-all ${
                      target === "all" ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "hover:bg-muted/50"
                    }`}
                  >
                    <RadioGroupItem value="all" id="target-all" />
                    <div className="flex-1">
                      <p className="font-semibold text-foreground text-sm">All Students</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Broadcast to your entire class</p>
                    </div>
                  </Label>
                  
                  <Label
                    htmlFor="target-selected"
                    className={`flex items-center space-x-3 border p-4 rounded-xl cursor-pointer transition-all ${
                      target === "selected" ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "hover:bg-muted/50"
                    }`}
                  >
                    <RadioGroupItem value="selected" id="target-selected" />
                    <div className="flex-1">
                      <p className="font-semibold text-foreground text-sm">Selected Students</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Target specific individuals</p>
                    </div>
                  </Label>
                </RadioGroup>

                {target === "selected" && (
                  <div className="mt-6 flex flex-col h-[300px] border rounded-xl overflow-hidden shadow-inner bg-muted/10 animate-in fade-in slide-in-from-top-2">
                    <div className="p-3 border-b bg-background">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          placeholder="Search students..."
                          className="pl-9 h-9 text-sm bg-muted/30 border-none focus-visible:ring-1"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto p-2">
                      {loadingStudents ? (
                        <div className="flex flex-col items-center justify-center h-full space-y-3 text-muted-foreground">
                          <Loader2 className="h-6 w-6 animate-spin text-primary/60" />
                          <p className="text-sm">Loading directory...</p>
                        </div>
                      ) : filteredStudents.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                          <UserCheck className="h-8 w-8 mb-2 opacity-20" />
                          <p className="text-sm">No students found.</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <div className="px-2 py-1.5 flex justify-between items-center mb-1">
                            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                              {filteredStudents.length} Students
                            </span>
                            <button 
                              type="button" 
                              onClick={selectAll}
                              className="text-xs text-primary hover:underline font-medium"
                            >
                              {studentIds.length === filteredStudents.length && filteredStudents.length > 0 
                                ? "Deselect All" 
                                : "Select All"}
                            </button>
                          </div>
                          {filteredStudents.map((student) => {
                            const isSelected = studentIds.includes(student.id);
                            return (
                              <label 
                                key={student.id} 
                                className={`flex items-center p-2.5 rounded-lg cursor-pointer transition-colors ${
                                  isSelected ? "bg-primary/10" : "hover:bg-muted/50"
                                }`}
                              >
                                <div className={`flex items-center justify-center w-5 h-5 rounded border mr-3 flex-shrink-0 transition-colors ${
                                  isSelected ? "bg-primary border-primary text-primary-foreground" : "border-input bg-background"
                                }`}>
                                  {isSelected && <Check className="w-3.5 h-3.5" />}
                                </div>
                                <div className="flex flex-col min-w-0">
                                  <span className="text-sm font-medium text-foreground truncate">{student.name}</span>
                                  <span className="text-xs text-muted-foreground truncate">{student.email}</span>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
              <CardFooter className="bg-muted/10 border-t p-6">
                 <Button
                  onClick={handleSend}
                  disabled={isSending || (target === 'selected' && studentIds.length === 0)}
                  className="w-full h-12 text-base font-medium shadow-md transition-all hover:shadow-lg"
                  size="lg"
                >
                  {isSending ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Dispatching...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Notification
                    </>
                  )}
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
